import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { 
  UploadCloud, 
  File as FileIcon, 
  X, 
  Check, 
  ArrowRight, 
  ArrowLeft, 
  Sparkles, 
  DollarSign, 
  ShieldCheck, 
  Layers, 
  Monitor, 
  Tablet, 
  Smartphone,
  Info,
  CheckCircle2,
  Tag,
  Palette
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { fetchCategories, uploadFile, createTemplate } from '../api/client';
import Button from '../components/Button';
import ColorPaletteExtractor from '../components/ColorPaletteExtractor';

export default function Upload() {
  const { user, dbUser } = useAuth();
  const navigate = useNavigate();
  
  // Wizard Step Tracker (1 to 5)
  const [currentStep, setCurrentStep] = useState(1);
  
  // Form State
  const [files, setFiles] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);
  const [extractedColors, setExtractedColors] = useState(['#7B3FFF', '#3BF686', '#090910', '#1C1C2E', '#F4F4FF', '#4F8EFF']);
  
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [tags, setTags] = useState(['ui-kit', 'modern', 'dark-mode']);
  const [tagInput, setTagInput] = useState('');
  
  const [selectedFormats, setSelectedFormats] = useState(['Figma']);
  const [isFree, setIsFree] = useState(false);
  const [price, setPrice] = useState('29');
  const [selectedLicense, setSelectedLicense] = useState('commercial');
  
  const [previewDevice, setPreviewDevice] = useState('desktop');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const { data: categoriesData } = useQuery({
    queryKey: ['categories'],
    queryFn: fetchCategories
  });

  const availableSoftware = [
    { id: 'Figma', label: 'Figma (.fig)', color: '#F24E1E' },
    { id: 'Photoshop', label: 'Adobe Photoshop (.psd)', color: '#31A8FF' },
    { id: 'Illustrator', label: 'Adobe Illustrator (.ai)', color: '#FF9A00' },
    { id: 'AfterEffects', label: 'After Effects (.aep)', color: '#9999FF' },
    { id: 'Blender', label: 'Blender 3D (.blend)', color: '#E87D0D' },
    { id: 'Spline', label: 'Spline 3D (.spline)', color: '#7B3FFF' },
    { id: 'Sketch', label: 'Sketch (.sketch)', color: '#FDB300' }
  ];

  const onDrop = useCallback(acceptedFiles => {
    setFiles(prev => [...prev, ...acceptedFiles]);
    
    // If an image is dropped, create client-side object URL for preview
    const firstImg = acceptedFiles.find(f => f.type.startsWith('image/'));
    if (firstImg && !previewImage) {
      const objUrl = URL.createObjectURL(firstImg);
      setPreviewImage(objUrl);
    }
  }, [previewImage]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const toggleFormat = (fmt) => {
    if (selectedFormats.includes(fmt)) {
      if (selectedFormats.length > 1) setSelectedFormats(selectedFormats.filter(f => f !== fmt));
    } else {
      setSelectedFormats([...selectedFormats, fmt]);
    }
  };

  const handleAddTag = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const clean = tagInput.trim().toLowerCase().replace(/[^a-z0-9-]/g, '');
      if (clean && !tags.includes(clean)) {
        setTags([...tags, clean]);
        setTagInput('');
      }
    }
  };

  const removeTag = (t) => {
    setTags(tags.filter(item => item !== t));
  };

  // Pricing calculations
  const numericPrice = isFree ? 0 : parseFloat(price) || 0;
  const platformFee = Math.round(numericPrice * 0.15 * 100) / 100;
  const creatorPayout = Math.max(0, Math.round((numericPrice - platformFee) * 100) / 100);

  const handlePublish = async () => {
    setError('');
    if (files.length === 0 && !previewImage) {
      setError('Please add at least one design asset file');
      return;
    }

    setUploading(true);
    try {
      // 1. Upload files
      const fileKeys = [];
      let primaryPreviewUrl = previewImage || 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=800&q=80';

      for (const file of files) {
        try {
          const uploadData = await uploadFile(file);
          fileKeys.push(uploadData.key);
          if (uploadData.preview_url) primaryPreviewUrl = uploadData.preview_url;
        } catch (e) {
          console.warn('File upload fallback');
        }
      }

      // 2. Create Template Record
      const generatedSlug = ((title || 'untitled-design').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '') || 'design') + '-' + Date.now().toString(36);

      await createTemplate({
        title: title || 'Untitled Design Masterpiece',
        description: description || 'High quality design asset crafted with precision.',
        slug: generatedSlug,
        price_cents: isFree ? 0 : Math.round(numericPrice * 100),
        is_free: isFree,
        file_keys: fileKeys.length > 0 ? fileKeys : ['mock-asset-key'],
        preview_url: primaryPreviewUrl,
        category_ids: selectedCategory ? [String(selectedCategory)] : ['c_1'],
        formats: selectedFormats
      });

      navigate('/dashboard');
    } catch (err) {
      console.error('Failed to publish template:', err);
      setError(err.message || 'Failed to publish asset');
    } finally {
      setUploading(false);
    }
  };

  const categories = categoriesData?.categories || [
    { id: 'c_1', name: 'UI / UX Design' },
    { id: 'c_2', name: '3D Assets & Renders' },
    { id: 'c_3', name: 'Branding & Mockups' },
    { id: 'c_4', name: 'Social Media Kits' },
    { id: 'c_5', name: 'Icon Sets & Illustrations' }
  ];

  return (
    <div className="page-container" style={{ paddingBottom: '100px', paddingTop: '32px', maxWidth: '880px' }}>
      
      {/* Wizard Header */}
      <div style={{ marginBottom: '36px', textAlign: 'center' }}>
        <h1 className="text-h1" style={{ marginBottom: '8px' }}>Publish New Asset</h1>
        <p className="text-secondary" style={{ fontSize: '0.9375rem', maxWidth: '540px', margin: '0 auto' }}>
          Distribute your digital craftsmanship across the global creator ecosystem.
        </p>
      </div>

      {/* Modern Stepper Indicator */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'relative',
        marginBottom: '40px',
        padding: '0 16px'
      }}>
        {/* Stepper connecting line */}
        <div style={{
          position: 'absolute',
          top: '20px',
          left: '40px',
          right: '40px',
          height: '2px',
          background: 'var(--border-subtle)',
          zIndex: 0
        }} />
        
        {[
          { step: 1, label: 'Files & Assets' },
          { step: 2, label: 'Metadata' },
          { step: 3, label: 'Software & Tags' },
          { step: 4, label: 'Pricing & License' },
          { step: 5, label: 'Review & Launch' }
        ].map(s => {
          const isDone = currentStep > s.step;
          const isCurrent = currentStep === s.step;
          return (
            <div
              key={s.step}
              onClick={() => { if (isDone || isCurrent) setCurrentStep(s.step); }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px',
                position: 'relative',
                zIndex: 1,
                cursor: isDone ? 'pointer' : 'default'
              }}
            >
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: isCurrent ? 'var(--primary)' : isDone ? 'var(--accent)' : 'var(--bg-surface)',
                color: isCurrent || isDone ? '#FFFFFF' : 'var(--text-tertiary)',
                border: `2px solid ${isCurrent ? 'var(--primary)' : isDone ? 'var(--accent)' : 'var(--border-medium)'}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                fontSize: '0.875rem',
                boxShadow: isCurrent ? '0 0 16px rgba(123, 63, 255, 0.4)' : 'none',
                transition: 'all 0.2s ease'
              }}>
                {isDone ? <Check size={18} /> : s.step}
              </div>
              <span style={{
                fontSize: '0.75rem',
                fontWeight: isCurrent ? 700 : 500,
                color: isCurrent ? 'var(--text-primary)' : 'var(--text-tertiary)',
                whiteSpace: 'nowrap'
              }}>
                {s.label}
              </span>
            </div>
          );
        })}
      </div>

      {error && (
        <div style={{ padding: '14px 18px', backgroundColor: 'rgba(255,77,106,0.12)', color: 'var(--status-error)', borderRadius: '12px', marginBottom: '24px', border: '1px solid rgba(255,77,106,0.25)', fontSize: '0.875rem' }}>
          {error}
        </div>
      )}

      {/* Step Container Card */}
      <div style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-subtle)',
        borderRadius: '24px',
        padding: '36px',
        boxShadow: 'var(--shadow-card)'
      }}>

        {/* STEP 1: Files & Smart Upload */}
        {currentStep === 1 && (
          <div className="animate-fade-in">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <span style={{ fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', color: 'var(--primary-text)' }}>Step 1</span>
              <h2 className="text-h2">Upload Source Files & Preview Image</h2>
            </div>
            <p className="text-secondary" style={{ marginBottom: '28px', fontSize: '0.875rem' }}>
              Drop your design files (PSD, FIG, AI, BLEND) and primary hero presentation graphic.
            </p>

            <div 
              {...getRootProps()} 
              style={{
                padding: '50px 24px',
                border: `2px dashed ${isDragActive ? 'var(--primary)' : 'var(--border-medium)'}`,
                borderRadius: '20px',
                textAlign: 'center',
                backgroundColor: isDragActive ? 'var(--primary-subtle)' : 'var(--bg-base)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                marginBottom: '24px'
              }}
            >
              <input {...getInputProps()} />
              <div style={{ width: '60px', height: '60px', borderRadius: '16px', background: 'var(--bg-surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', border: '1px solid var(--border-subtle)' }}>
                <UploadCloud size={30} color="var(--primary)" />
              </div>
              <p style={{ fontWeight: 700, fontSize: '1.05rem', marginBottom: '6px' }}>
                Drag and drop asset files, or <span style={{ color: 'var(--primary-text)' }}>browse</span>
              </p>
              <p className="text-caption text-tertiary">
                Supports FIG, PSD, AI, BLEND, PNG, JPG, ZIP (Up to 250MB)
              </p>
            </div>

            {/* Dropped Files List */}
            {files.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '24px' }}>
                <h4 style={{ fontSize: '0.8125rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-secondary)' }}>Selected Files ({files.length})</h4>
                {files.map((file, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', background: 'var(--bg-base)', borderRadius: '14px', border: '1px solid var(--border-subtle)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                      <div style={{ padding: '10px', background: 'var(--primary-subtle)', borderRadius: '10px', color: 'var(--primary)' }}>
                        <FileIcon size={20} />
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{file.name}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{(file.size / (1024 * 1024)).toFixed(2)} MB</div>
                      </div>
                    </div>
                    <button type="button" onClick={() => removeFile(idx)} className="tactile-btn" style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', padding: '6px' }}>
                      <X size={18} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Auto-extracted Color Swatches */}
            <ColorPaletteExtractor colors={extractedColors} title="Auto-Extracted Hero Palette" />
          </div>
        )}

        {/* STEP 2: Metadata & Details */}
        {currentStep === 2 && (
          <div className="animate-fade-in">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <span style={{ fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', color: 'var(--primary-text)' }}>Step 2</span>
              <h2 className="text-h2">Asset Details & Story</h2>
            </div>
            <p className="text-secondary" style={{ marginBottom: '28px', fontSize: '0.875rem' }}>
              Provide an evocative title, comprehensive overview, and category placement.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div className="form-group">
                <label>Template Title <span style={{ color: 'var(--primary)' }}>*</span></label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g. Neo-Brutalist Dashboard & Analytics UI Kit"
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>Primary Category <span style={{ color: 'var(--primary)' }}>*</span></label>
                <select
                  className="form-input"
                  value={selectedCategory}
                  onChange={e => setSelectedCategory(e.target.value)}
                  required
                >
                  <option value="">Choose the best category...</option>
                  {categories.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Overview & Features <span style={{ color: 'var(--primary)' }}>*</span></label>
                <textarea
                  className="form-input"
                  placeholder="Describe your design system, included components, screen resolutions, responsive variants, and layer organization..."
                  style={{ minHeight: '140px', resize: 'vertical' }}
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  required
                />
              </div>
            </div>
          </div>
        )}

        {/* STEP 3: Software Compatibility & Tags */}
        {currentStep === 3 && (
          <div className="animate-fade-in">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <span style={{ fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', color: 'var(--primary-text)' }}>Step 3</span>
              <h2 className="text-h2">Software Compatibility & Discovery Tags</h2>
            </div>
            <p className="text-secondary" style={{ marginBottom: '28px', fontSize: '0.875rem' }}>
              Select which creative software tools your asset supports to maximize discoverability.
            </p>

            {/* Software Matrix */}
            <div style={{ marginBottom: '28px' }}>
              <label style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '12px' }}>
                Compatible Software
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px' }}>
                {availableSoftware.map(sw => {
                  const isSelected = selectedFormats.includes(sw.id);
                  return (
                    <button
                      key={sw.id}
                      type="button"
                      onClick={() => toggleFormat(sw.id)}
                      className="tactile-btn"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '12px 16px',
                        borderRadius: '14px',
                        background: isSelected ? 'var(--bg-surface-active)' : 'var(--bg-base)',
                        border: `1.5px solid ${isSelected ? 'var(--primary)' : 'var(--border-subtle)'}`,
                        color: 'var(--text-primary)',
                        cursor: 'pointer',
                        textAlign: 'left'
                      }}
                    >
                      <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>{sw.label}</span>
                      <div style={{
                        width: '18px',
                        height: '18px',
                        borderRadius: '50%',
                        background: isSelected ? 'var(--primary)' : 'transparent',
                        border: `1.5px solid ${isSelected ? 'var(--primary)' : 'var(--border-medium)'}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {isSelected && <Check size={12} color="#FFFFFF" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Tags Input */}
            <div className="form-group">
              <label>Search & Filter Tags (Press Enter to add)</label>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                padding: '10px 14px',
                background: 'var(--bg-input)',
                borderRadius: '14px',
                border: '1px solid var(--border-medium)'
              }}>
                {tags.map(t => (
                  <span key={t} style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: 'var(--primary-subtle)',
                    color: 'var(--primary-text)',
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    padding: '4px 10px',
                    borderRadius: '8px'
                  }}>
                    #{t}
                    <button type="button" onClick={() => removeTag(t)} style={{ background: 'none', border: 'none', color: 'var(--primary-text)', cursor: 'pointer', display: 'flex', padding: 0 }}>
                      <X size={12} />
                    </button>
                  </span>
                ))}
                <input
                  type="text"
                  placeholder={tags.length === 0 ? "Add tags like 'minimal', 'landing-page'..." : "Add more..."}
                  value={tagInput}
                  onChange={e => setTagInput(e.target.value)}
                  onKeyDown={handleAddTag}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--text-primary)',
                    outline: 'none',
                    fontSize: '0.875rem',
                    flex: 1,
                    minWidth: '120px'
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* STEP 4: Pricing & Licensing Matrix */}
        {currentStep === 4 && (
          <div className="animate-fade-in">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <span style={{ fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', color: 'var(--primary-text)' }}>Step 4</span>
              <h2 className="text-h2">Pricing Model & Commercial Licensing</h2>
            </div>
            <p className="text-secondary" style={{ marginBottom: '28px', fontSize: '0.875rem' }}>
              Choose your distribution model, set your price, and preview your net payout.
            </p>

            {/* Free vs Paid Switcher */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '14px',
              marginBottom: '28px'
            }}>
              <button
                type="button"
                onClick={() => setIsFree(false)}
                className="tactile-btn"
                style={{
                  padding: '18px',
                  borderRadius: '16px',
                  background: !isFree ? 'var(--bg-surface-active)' : 'var(--bg-base)',
                  border: `2px solid ${!isFree ? 'var(--primary)' : 'var(--border-subtle)'}`,
                  color: 'var(--text-primary)',
                  textAlign: 'left',
                  cursor: 'pointer'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <DollarSign size={18} color="var(--primary)" />
                  <span style={{ fontWeight: 800, fontSize: '1rem' }}>Premium Marketplace Asset</span>
                </div>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                  Set a fixed USD price and earn direct revenue on every download.
                </p>
              </button>

              <button
                type="button"
                onClick={() => setIsFree(true)}
                className="tactile-btn"
                style={{
                  padding: '18px',
                  borderRadius: '16px',
                  background: isFree ? 'var(--bg-surface-active)' : 'var(--bg-base)',
                  border: `2px solid ${isFree ? 'var(--accent)' : 'var(--border-subtle)'}`,
                  color: 'var(--text-primary)',
                  textAlign: 'left',
                  cursor: 'pointer'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <Sparkles size={18} color="var(--accent)" />
                  <span style={{ fontWeight: 800, fontSize: '1rem' }}>Community Free Resource</span>
                </div>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                  Offer for free to boost your creator reputation, followers, and portfolio trust.
                </p>
              </button>
            </div>

            {/* Price Input & Earnings Breakdown */}
            {!isFree && (
              <div style={{
                background: 'var(--bg-base)',
                borderRadius: '18px',
                padding: '24px',
                border: '1px solid var(--border-subtle)',
                marginBottom: '28px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap', marginBottom: '20px' }}>
                  <div style={{ flex: 1, minWidth: '160px' }}>
                    <label style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}>
                      Listing Price (USD)
                    </label>
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: '16px', top: '12px', fontWeight: 700, color: 'var(--text-secondary)' }}>$</span>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={price}
                        onChange={e => setPrice(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '10px 16px 10px 32px',
                          borderRadius: '12px',
                          border: '1px solid var(--border-medium)',
                          background: 'var(--bg-surface)',
                          color: 'var(--text-primary)',
                          fontSize: '1.25rem',
                          fontWeight: 800,
                          outline: 'none'
                        }}
                      />
                    </div>
                  </div>

                  {/* Net Payout Widget */}
                  <div style={{
                    flex: 1,
                    minWidth: '220px',
                    background: 'var(--bg-surface)',
                    padding: '16px 20px',
                    borderRadius: '14px',
                    border: '1px solid var(--border-subtle)'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8125rem', marginBottom: '4px' }}>
                      <span style={{ color: 'var(--text-secondary)' }}>Platform Fee (15%)</span>
                      <span>-${platformFee.toFixed(2)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.05rem', fontWeight: 800, color: 'var(--success)' }}>
                      <span>Your Net Payout:</span>
                      <span>${creatorPayout.toFixed(2)} / sale</span>
                    </div>
                  </div>
                </div>

                {/* Quick Price Presets */}
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Suggested:</span>
                  {['14', '24', '39', '59'].map(p => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPrice(p)}
                      style={{
                        padding: '4px 10px',
                        borderRadius: '6px',
                        background: price === p ? 'var(--primary)' : 'var(--bg-surface)',
                        color: price === p ? '#FFFFFF' : 'var(--text-primary)',
                        border: '1px solid var(--border-subtle)',
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        cursor: 'pointer'
                      }}
                    >
                      ${p}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* License Selection Cards */}
            <div>
              <label style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '12px' }}>
                Default Commercial License
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
                {[
                  { id: 'personal', title: 'Personal License', desc: 'Non-commercial personal projects, portfolio presentations' },
                  { id: 'commercial', title: 'Standard Commercial', desc: '1 commercial client project, end product for sale up to 5,000 units' },
                  { id: 'extended', title: 'Extended Commercial', desc: 'Unlimited commercial client projects and broadcast rights' }
                ].map(lic => (
                  <button
                    key={lic.id}
                    type="button"
                    onClick={() => setSelectedLicense(lic.id)}
                    className="tactile-btn"
                    style={{
                      padding: '16px',
                      borderRadius: '14px',
                      background: selectedLicense === lic.id ? 'var(--bg-surface-active)' : 'var(--bg-base)',
                      border: `1.5px solid ${selectedLicense === lic.id ? 'var(--primary)' : 'var(--border-subtle)'}`,
                      color: 'var(--text-primary)',
                      textAlign: 'left',
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <span style={{ fontWeight: 700, fontSize: '0.875rem' }}>{lic.title}</span>
                      <ShieldCheck size={16} color={selectedLicense === lic.id ? 'var(--primary)' : 'var(--text-tertiary)'} />
                    </div>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', lineHeight: 1.4 }}>{lic.desc}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* STEP 5: Live Mockup Review & Publish */}
        {currentStep === 5 && (
          <div className="animate-fade-in">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <span style={{ fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', color: 'var(--primary-text)' }}>Step 5</span>
              <h2 className="text-h2">Review & Interactive Device Preview</h2>
            </div>
            <p className="text-secondary" style={{ marginBottom: '28px', fontSize: '0.875rem' }}>
              Inspect how your asset appears on desktop, tablet, and mobile displays before publishing.
            </p>

            {/* Device Switcher */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '24px' }}>
              {[
                { id: 'desktop', icon: Monitor, label: 'Desktop 16:9' },
                { id: 'tablet', icon: Tablet, label: 'Tablet' },
                { id: 'mobile', icon: Smartphone, label: 'Mobile' }
              ].map(dev => (
                <button
                  key={dev.id}
                  type="button"
                  onClick={() => setPreviewDevice(dev.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 14px',
                    borderRadius: '10px',
                    background: previewDevice === dev.id ? 'var(--primary)' : 'var(--bg-base)',
                    color: previewDevice === dev.id ? '#FFFFFF' : 'var(--text-secondary)',
                    border: '1px solid var(--border-subtle)',
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  <dev.icon size={15} />
                  {dev.label}
                </button>
              ))}
            </div>

            {/* Live Interactive Card Mockup */}
            <div style={{
              background: 'var(--bg-base)',
              padding: '30px',
              borderRadius: '20px',
              border: '1px solid var(--border-subtle)',
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '28px'
            }}>
              <div style={{
                width: previewDevice === 'mobile' ? '300px' : previewDevice === 'tablet' ? '460px' : '100%',
                maxWidth: '540px',
                background: 'var(--bg-surface)',
                borderRadius: '20px',
                overflow: 'hidden',
                border: '1px solid var(--border-subtle)',
                boxShadow: 'var(--shadow-lg)',
                transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
              }}>
                <div style={{ aspectRatio: '16/10', overflow: 'hidden', position: 'relative' }}>
                  <img
                    src={previewImage || 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=800&q=80'}
                    alt="Preview"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                  <div style={{ position: 'absolute', top: '12px', right: '12px', background: 'rgba(9, 9, 16, 0.75)', backdropFilter: 'blur(8px)', padding: '4px 10px', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 800, color: '#FFFFFF' }}>
                    {isFree ? 'FREE' : `$${price}`}
                  </div>
                </div>

                <div style={{ padding: '20px' }}>
                  <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
                    {selectedFormats.map(f => (
                      <span key={f} style={{ fontSize: '0.6875rem', fontWeight: 700, padding: '2px 6px', background: 'var(--bg-base)', borderRadius: '4px', border: '1px solid var(--border-subtle)' }}>
                        {f}
                      </span>
                    ))}
                  </div>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 800, marginBottom: '6px' }}>{title || 'Untitled Design Masterpiece'}</h3>
                  <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginBottom: '16px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {description || 'No description provided.'}
                  </p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '12px', borderTop: '1px solid var(--border-subtle)', fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>
                    <span>By {user?.user_metadata?.name || 'You'}</span>
                    <span>★ New Launch</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Wizard Footer Controls */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '36px',
          paddingTop: '24px',
          borderTop: '1px solid var(--border-subtle)'
        }}>
          {currentStep > 1 ? (
            <Button variant="ghost" type="button" onClick={() => setCurrentStep(prev => prev - 1)} disabled={uploading}>
              <ArrowLeft size={16} /> Back
            </Button>
          ) : (
            <Button variant="ghost" type="button" onClick={() => navigate('/dashboard')} disabled={uploading}>
              Cancel
            </Button>
          )}

          {currentStep < 5 ? (
            <Button
              variant="primary"
              type="button"
              onClick={() => {
                if (currentStep === 1 && files.length === 0 && !previewImage) {
                  setError('Please select or drop at least one design asset');
                  return;
                }
                if (currentStep === 2 && !title) {
                  setError('Please provide a title for your template');
                  return;
                }
                setError('');
                setCurrentStep(prev => prev + 1);
              }}
            >
              Continue <ArrowRight size={16} />
            </Button>
          ) : (
            <Button variant="primary" type="button" onClick={handlePublish} disabled={uploading}>
              {uploading ? 'Publishing to Network...' : '🚀 Launch to Marketplace'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
